import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';
import { EmptyState } from '@/shared/components/feedback/EmptyState/EmptyState';
import { Skeleton } from '@/shared/components/feedback/Skeleton/Skeleton';
import { Button } from '@/shared/components/ui/Button/Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/Dialog/Dialog';
import { Label } from '@/shared/components/ui/Label/Label';
import { Textarea } from '@/shared/components/ui/Textarea/Textarea';
import { useBrand } from '@/shared/hooks/useBrand/useBrand';
import { useDirection } from '@/shared/hooks/useDirection/useDirection';
import { formatDateTime } from '@/shared/utils/format-datetime/format-datetime.util';

import { signalLabel } from '../../utils/signal-labels/signal-labels.util';

import { useConfirmMatchModalLogic } from './useConfirmMatchModalLogic';

/**
 * Confirming an identity match — the only control in the portal that **ends an
 * account**, and the dialog is built to be read rather than clicked through.
 *
 * It names both accounts, says why the other one was banned, shows what else the
 * platform captured for this member, states that the ban is terminal, and takes
 * a staff note before it will do anything. A generic yes/no confirm could carry
 * none of that, which is exactly why this is not one.
 *
 * When the match blocked a registration that never became an account there is
 * **nothing to ban**: the dialog says so and sends the reviewer to `dismiss`
 * instead of offering a button the api is certain to refuse.
 */
export function ConfirmMatchModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation('trust');
  const { locale } = useDirection();
  const { formats } = useBrand();
  const {
    target,
    hasNothingToBan,
    note,
    setNote,
    signals,
    isLoadingSignals,
    didSignalsFail,
    isBanning,
    error,
    submit,
  } = useConfirmMatchModalLogic(modalData, onCancel);

  if (target === null) {
    return null;
  }

  const subject = target.subjectEmail ?? t('queue.noAccount');
  const matched = target.matchedEmail ?? t('queue.missingAccount');

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onCancel();
        }
      }}
    >
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>
            {hasNothingToBan ? t('confirm.noSubject.title') : t('confirm.title')}
          </DialogTitle>
          <DialogDescription>{t('queue.subtitle')}</DialogDescription>
        </DialogHeader>

        <DialogBody>
          {hasNothingToBan ? (
            <p className="text-body text-foreground">{t('confirm.noSubject.body')}</p>
          ) : (
            <>
              {/* The claim, in one sentence. The two addresses are repeated in
                  the box below with `dir="ltr"` rather than emphasised inline:
                  an email is not localised, and a bidi run inside an Arabic
                  sentence puts the @ somewhere confusing. */}
              <p className="text-body text-foreground">
                {t('confirm.lead', {
                  subject,
                  matched,
                  signal: signalLabel(t, target.signalType),
                })}
              </p>

              <div className="flex flex-col gap-xs rounded-md bg-muted p-md">
                <span dir="ltr" className="text-body font-medium">
                  {subject}
                </span>
                <span dir="ltr" className="text-caption text-muted-foreground">
                  {matched}
                </span>
                <span className="text-caption text-muted-foreground">
                  {target.matchedReason === null
                    ? t('confirm.matchedNoReason')
                    : t('confirm.matchedReason', { reason: target.matchedReason })}
                </span>
              </div>

              {/* Terminal, said out loud. `restore` deliberately does not apply
                  to a ban (N9), and an operator who assumed otherwise would find
                  that out too late. */}
              <p className="flex items-start gap-sm text-body text-destructive">
                <AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{t('confirm.terminal')}</span>
              </p>

              <section className="flex flex-col gap-sm">
                <h3 className="text-caption font-medium text-foreground">{t('signals.title')}</h3>

                {isLoadingSignals ? (
                  <Skeleton className="h-16 w-full" />
                ) : didSignalsFail ? (
                  <p className="text-caption text-muted-foreground">{t('signals.loadFailed')}</p>
                ) : signals.length === 0 ? (
                  <EmptyState title={t('signals.empty')} />
                ) : (
                  <ul className="flex flex-col gap-xs">
                    {signals.map((signal) => (
                      <li
                        key={signal.id}
                        className="flex flex-wrap items-baseline justify-between gap-sm text-caption"
                      >
                        <span className="text-foreground">{signalLabel(t, signal.signalType)}</span>
                        <span className="text-muted-foreground">
                          {t('signals.capturedAt', {
                            when: formatDateTime(signal.capturedAt, locale, formats),
                          })}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <div className="flex flex-col gap-xs">
                <Label htmlFor="trust-confirm-note">{t('confirm.noteLabel')}</Label>
                <Textarea
                  id="trust-confirm-note"
                  aria-describedby="trust-confirm-note-hint"
                  value={note}
                  maxLength={2000}
                  onChange={(event) => {
                    setNote(event.target.value);
                  }}
                />
                <p id="trust-confirm-note-hint" className="text-caption text-muted-foreground">
                  {t('confirm.noteHint')}
                </p>
              </div>

              {error === null ? null : (
                <p role="alert" className="text-caption text-destructive">
                  {error}
                </p>
              )}
            </>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>
            {t('queue.action.dismiss')}
          </Button>

          {hasNothingToBan ? null : (
            <Button
              variant="destructive"
              isLoading={isBanning}
              onClick={() => {
                void submit();
              }}
            >
              {t('confirm.submit')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
